/**
 * @legacy — do not add new events here. It will be deprecated in the future.
 * See the Analytics section in README.md for the new pattern.
 */
import sumBy from 'lodash/sumBy';

import { GTM_CONTAINER_ID } from 'src/config/constants';
import { type ListingProduct, ListingProductCode } from 'src/generated/graphql';
import type { UserRegistrationType } from 'src/types/userRegistrationType';

const ADROLL_SEGMENTS_ID = '25ec50e5';

export enum AnalyticsEvent {
  ExperimentInteraction = 'experimentInteraction',
  ListingCoreProductPurchased = 'listingCoreProductPurchased',
  OnboardingComplete = 'onboardingComplete',
  OnboardingCompleteSem = 'onboardingCompleteSem',
  PageExited = 'pageExited',
  PaymentSuccess = 'paymentSuccess',
  PrivateSellerSelectedProductLoggedIn = 'privateSellerSelectedProductLoggedIn',
  RecommendationRelevantNo = 'recommendationRelevantNo',
  RecommendationRelevantYes = 'recommendationRelevantYes',
  RegistrationCompleteSem = 'registrationCompleteSem',
  RegistrationStartSem = 'registrationStartSem',
  SellerEnquiriesShow = 'sellerEnquiriesShow',
  UserLogin = 'userLogin',
}

// Ensure this remains private (i.e. do not export)
// We alias `window.heap.track` so that we can easily track invocations
const fireHeapEvent = (event: AnalyticsEvent, properties: Record<any, any>) => {
  window.heap.track(event, properties);
};

// Ensure this remains private (i.e. do not export)
// We alias `window.dataLayer.push` so that we can easily track invocations
const fireDataLayerEvent = (
  event: AnalyticsEvent,
  properties: Record<any, any>,
) => {
  window.dataLayer ||= [];
  window.dataLayer.push({ event, ...properties });
};

export const googleEventCallback = (
  resolve: (resolvedValue: boolean) => void,
  gtmId: string,
) => {
  if (gtmId === GTM_CONTAINER_ID) {
    resolve(true);
  }
};

export const fireExperimentInteractionEvent = (properties: {
  experiment: string;
  variant: string;
}) => {
  fireHeapEvent(AnalyticsEvent.ExperimentInteraction, properties);
};

export const firePaymentSuccessEvent = (products: ListingProduct[]) => {
  const value = sumBy(products, 'cost') / 100;
  // Heap does not understand arrays of objects, so this needs to be flattened
  const productCodes = products.map((p) => p.productCode).join(',');

  fireHeapEvent(AnalyticsEvent.PaymentSuccess, {
    value,
    products: productCodes,
  });

  products.forEach((product) => {
    fireListingCoreProductPurchaseEvent(product.productCode);
  });

  window.__adroll?.record_user({ adroll_segments: ADROLL_SEGMENTS_ID });

  fireDataLayerEvent(AnalyticsEvent.PaymentSuccess, {
    value,
    products,
  });
};

export const fireOnboardingCompleteEvent = async (
  registrationType: UserRegistrationType,
): Promise<boolean> =>
  new Promise((resolve) => {
    fireHeapEvent(AnalyticsEvent.OnboardingComplete, {
      registrationType,
    });
    fireDataLayerEvent(AnalyticsEvent.OnboardingComplete, {
      registrationType,
      eventCallback: (gtmId: string) => googleEventCallback(resolve, gtmId),
    });

    // This acts as a safeguard, if googleEvent.eventCallback is unreliable
    // Which forces a promise resolution if googleEvent.eventCallback is unresponsive
    setTimeout(() => {
      resolve(true);
    }, 2000);

    return true;
  });

export const fireOnboardingCompleteSemEvent = async (
  registrationType: UserRegistrationType,
): Promise<boolean> =>
  new Promise((resolve) => {
    fireDataLayerEvent(AnalyticsEvent.OnboardingCompleteSem, {
      registrationType,
      eventCallback: (gtmId: string) => googleEventCallback(resolve, gtmId),
    });

    // This acts as a safeguard, if googleEvent.eventCallback is unreliable
    // Which forces a promise resolution if googleEvent.eventCallback is unresponsive
    setTimeout(() => {
      resolve(true);
    }, 2000);

    return true;
  });

export const fireRecommendationFeedbackEvent = (
  event:
    | AnalyticsEvent.RecommendationRelevantYes
    | AnalyticsEvent.RecommendationRelevantNo,
  buyerId: string,
  listingId: string,
) => {
  fireHeapEvent(event, { buyerId, listingId });
};

export const fireRegistrationEvent = async (
  event:
    | AnalyticsEvent.RegistrationStartSem
    | AnalyticsEvent.RegistrationCompleteSem,
): Promise<boolean> =>
  new Promise((resolve) => {
    fireDataLayerEvent(event, {
      eventCallback: (gtmId: string) => googleEventCallback(resolve, gtmId),
    });

    // This acts as a safeguard, if googleEvent.eventCallback is unreliable
    // Which forces a promise resolution if googleEvent.eventCallback is unresponsive
    setTimeout(() => {
      resolve(true);
    }, 2000);

    return true;
  });

export const fireListingCoreProductPurchaseEvent = (
  productCode: ListingProductCode,
) => {
  const postAnalyticsEvent = (
    name: AnalyticsEvent,
    properties: Record<string, string | number>,
  ) => {
    fireHeapEvent(name, properties);
    fireDataLayerEvent(name, properties);
  };

  switch (productCode) {
    // ========================================================================
    // Supported products
    // ========================================================================
    case ListingProductCode.Freemium: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.Freemium,
      });
      return;
    }
    case ListingProductCode.Standard: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.Standard,
      });
      return;
    }
    case ListingProductCode.StandardAndSearchBoost: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.Standard,
      });
      return;
    }
    case ListingProductCode.Accelerate: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.Accelerate,
      });
      return;
    }

    // ========================================================================
    // Deprecated
    // ========================================================================
    case ListingProductCode.SixAdPack: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.SixAdPack,
      });
      return;
    }
    case ListingProductCode.SixAdPackAndSearchBoost: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.SixAdPack,
      });
      return;
    }
    case ListingProductCode.ListUntilSold: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.ListUntilSold,
      });
      return;
    }
    case ListingProductCode.ListUntilSoldAndSearchBoostLus: {
      postAnalyticsEvent(AnalyticsEvent.ListingCoreProductPurchased, {
        productCode: ListingProductCode.ListUntilSold,
      });
      return;
    }

    // ========================================================================
    // Depth products
    // ========================================================================
    case ListingProductCode.BoostedAd:
    case ListingProductCode.BoostedAdLus:
    case ListingProductCode.EmailBoost:
    case ListingProductCode.EmailBoostLus:
    case ListingProductCode.EmailBoostAndSearchBoost:
    case ListingProductCode.EmailBoostAndSearchBoostLus:
    case ListingProductCode.Unknown: {
      // Do nothing as this is a depth product
      return;
    }

    default: {
      throw new Error(
        `Unsupported product code ${productCode} for analytics event`,
      );
    }
  }
};

export const fireLoggedInEvent = (loginStatus: 'LOGGED_IN' | 'LOGGED_OUT') => {
  fireDataLayerEvent(AnalyticsEvent.UserLogin, { loginStatus });
};

export const fireViewEnquiriesEvent = (enquiryCount: number) => {
  fireHeapEvent(AnalyticsEvent.SellerEnquiriesShow, { enquiryCount });
};

export const fireSelectedPrivateSellerSelectedProductLoggedInEvent = (
  product: ListingProduct,
) => {
  let selectedProductName: string;
  // Everything has to be renamed to appropriate codes for heap to sync with .NET's event
  // Refer to: https://github.com/SEEK-Jobs/sbiz-seek-business/blob/master/websites/SeekBusiness.BizSeeker/Views/Shared/PrivateSellerInfo/_packages3Choice.cshtml#L327
  // No need to define for the other bundled products as that is on checkout. These are only for the offerred base products on /sell/packages
  switch (product.productCode) {
    case ListingProductCode.SixAdPack:
      selectedProductName = 'sixmonthadpack';
      break;
    case ListingProductCode.ListUntilSold:
      selectedProductName = 'listuntilsold';
      break;
    case ListingProductCode.Freemium:
      selectedProductName = 'freemium';
      break;
    case ListingProductCode.Standard:
      selectedProductName = 'standard';
      break;
    case ListingProductCode.Accelerate:
      selectedProductName = 'accelerate';
      break;
    default:
      selectedProductName = product.description;
  }

  // Always send upper case as per .NET
  const selectedProductToken = product.productToken.toUpperCase();

  fireHeapEvent(AnalyticsEvent.PrivateSellerSelectedProductLoggedIn, {
    SelectedProductToken: selectedProductToken,
    SelectedProductName: selectedProductName,
  });
  fireDataLayerEvent(AnalyticsEvent.PrivateSellerSelectedProductLoggedIn, {
    SelectedProductToken: selectedProductToken,
    SelectedProductName: selectedProductName,

    selectedProductToken,
    selectedProductName,
  });
};
